import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import client from 'utils/client';
import { StatusPill } from 'components/StatusPill/StatusPill';
import { useToast } from 'components/Toast/useToast';
import ModalComponent from 'components/Modal/Modal';
import { useModal } from 'components/Modal/useModal';

const fmt = (ts) => dayjs(ts).format('MMM DD, YYYY h:mma');

// POST only enqueues; the worker writes the invite row at send time, so re-read a
// little later rather than straight away.
const REFETCH_AFTER_SEND_MS = 6000;

function Row({ label, children }) {
  return (
    <div className='md-portal-row'>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// Staff view of a merchant's portal login. There is no password anywhere in this
// flow: staff send an invite and the merchant chooses their own.
function PortalAccessCard({ merchantId, onEditEmail }) {
  const { onOpen } = useToast();
  const { isOpen, onOpen: openConfirm, onClose: closeConfirm } = useModal();
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const refetchTimer = useRef(null);

  const load = useCallback(() => client
    .get(`/merchants/${merchantId}/portal-access`)
    .then((res) => { setStatus(res.data.data); setLoadError(null); })
    .catch((e) => { setStatus(null); setLoadError(e?.message || 'Failed to load portal access'); }),
  [merchantId]);

  // The modal flag is app-wide. Clear a flag another page left set, so Revoke can
  // never open unbidden on arrival, and never leave it set behind us, or Staff and
  // Transactions would open their own modals.
  useEffect(() => {
    closeConfirm();
    return closeConfirm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStatus(null);
    setLoadError(null);
    load();
    return () => clearTimeout(refetchTimer.current);
  }, [load]);

  // Refetch whether the request succeeded or not: a failure usually means the state
  // changed underneath us, which is exactly when the card has to be right.
  const act = (request, successMessage, failureMessage, onSuccess) => {
    setBusy(true);
    return request()
      .then(
        (res) => {
          onOpen({ type: 'success', message: successMessage(res) });
          if (onSuccess) onSuccess();
        },
        (e) => onOpen({ type: 'error', message: e?.message || failureMessage }),
      )
      .then(load)
      .finally(() => setBusy(false));
  };

  const sendInvite = () => act(
    () => client.post(`/merchants/${merchantId}/portal-access`),
    (res) => `Invite sent to ${res.data?.data?.email || status?.email}`,
    'Failed to send invite',
    () => {
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(load, REFETCH_AFTER_SEND_MS);
    },
  );
  const revoke = () => {
    closeConfirm();
    return act(
      () => client.delete(`/merchants/${merchantId}/portal-access`),
      () => 'Portal access revoked',
      'Failed to revoke access',
      () => clearTimeout(refetchTimer.current),
    );
  };

  let body;
  if (loadError) {
    body = (
      <div className='md-portal-body'>
        <p className='md-portal-note md-portal-note--danger'>{loadError}</p>
        <div className='md-portal-actions'>
          <button type='button' className='btn-secondary' onClick={load}>Retry</button>
        </div>
      </div>
    );
  } else if (!status) {
    body = <div className='md-loading' style={{ height: 80 }}>Loading…</div>;
  } else {
    const s = status;
    const active = s.provisioned;
    // Revoked, and no invite sent since: staff cut this merchant off on purpose. This
    // is not the same as an invite that simply ran out, so it gets its own state.
    // Revoked with no invite since, or an invite already used and now off: either way
    // access ended deliberately, which is not the same as an invite that ran out.
    const revoked = !active && !!s.portal_revoked_at
      && !(s.invited_at && dayjs(s.invited_at).isAfter(dayjs(s.portal_revoked_at)));
    const invited = !!s.invited_at && !s.invite_used_at && !active && !revoked;
    const ended = revoked || (!active && !!s.invite_used_at);
    // invite_expires_at carries the invalidation time for an invite revoked or replaced
    // unused, so only a future value is known to be live.
    const linkLive = invited && !!s.invite_expires_at && dayjs(s.invite_expires_at).isAfter(dayjs());
    const canSend = !active && !!s.email;
    const sendLabel = invited ? 'Resend invite' : 'Send invite';

    let pill;
    if (active) pill = <StatusPill label='Active' variant='success' />;
    else if (invited) pill = <StatusPill label='Invited' variant='warning' />;
    else if (ended) pill = <StatusPill label='Access ended' variant='neutral' />;
    else pill = <StatusPill label='Not invited' variant='neutral' />;

    body = (
      <div className='md-portal-body'>
        <div>{pill}</div>

        {!s.email ? (
          <p className='md-portal-note md-portal-note--danger'>
            This merchant has no email address, so there is nowhere to send an invite. Add one to continue.
          </p>
        ) : null}

        <dl className='md-portal-rows'>
          <Row label='Email'>{s.email || '—'}</Row>
          {invited ? (
            <>
              <Row label='Invite sent'>
                {fmt(s.invited_at)}{s.invited_by ? ` by ${s.invited_by}` : ''}
              </Row>
              <Row label='Link'>
                {linkLive
                  ? `Not used yet. Valid until ${fmt(s.invite_expires_at)}`
                  : 'Not used, and no longer usable (it expired, or was revoked or replaced)'}
              </Row>
            </>
          ) : null}
          {active || ended ? (
            <Row label='Last sign-in'>{s.last_login_at ? fmt(s.last_login_at) : 'Never'}</Row>
          ) : null}
          {active ? <Row label='Branches'>{s.branch_count}</Row> : null}
        </dl>

        {ended ? (
          <p className='md-portal-note'>
            {revoked
              ? `Portal access was revoked by staff on ${fmt(s.portal_revoked_at)}. This was deliberate, not an expired invite.`
              : 'Portal access is switched off.'}
            {' '}Send a new invite to restore it.
          </p>
        ) : null}

        {active ? (
          <p className='md-portal-note'>
            If the merchant has lost their password, they can reset it themselves with &quot;Trouble signing in?&quot;
            on the sign-in page. Revoking access is not a password reset.
          </p>
        ) : null}

        <div className='md-portal-actions'>
          {!s.email ? (
            <button type='button' className='btn-primary' onClick={onEditEmail}>Edit merchant email</button>
          ) : null}
          {canSend ? (
            <button
              type='button'
              className={!s.email ? 'btn-secondary' : 'btn-primary'}
              onClick={sendInvite}
              disabled={busy}>
              {sendLabel}
            </button>
          ) : null}
          {active ? (
            <button type='button' className='btn-secondary' onClick={openConfirm} disabled={busy}>Revoke access</button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className='md-card md-portal-card'>
      <div className='md-card-head'>
        <div className='md-card-title'>Portal access</div>
        <div className='md-card-hint'>The merchant sets their own password from the invite link. Staff never see one.</div>
      </div>
      {body}
      {isOpen && status?.provisioned ? (
        <ModalComponent title='Revoke portal access?' styles={{ maxWidth: 440 }}>
          <p className='md-portal-note md-portal-note--danger'>
            This removes portal access for {status?.email || 'this merchant'}, covering every branch that uses that
            email. Any invite link still outstanding stops working, and they cannot sign in again until a new invite
            is sent and they set a new password.
          </p>
          <div className='md-portal-actions' style={{ marginTop: 16 }}>
            <button type='button' className='btn-danger' onClick={revoke} disabled={busy}>Revoke access</button>
            <button type='button' className='btn-secondary' onClick={closeConfirm}>Cancel</button>
          </div>
        </ModalComponent>
      ) : null}
    </div>
  );
}

export default PortalAccessCard;
